import React, { useMemo, useState } from "react";
import { Platform, Text, View } from "react-native";
import Svg, { Circle, Line, Path } from "react-native-svg";
import tw from "twrnc";

import { colors, text } from "@/components/settings/theme";
import { t, tn } from "@/localization";
import { atEventTime, formatCount, yTicks } from "./logic";

// Live viewers per minute for one stretch of live viewing (logic.liveSessions):
// a 2px line over a 10% wash, solid hairline grid, the peak marked and
// labelled. Hover (web) or touch and drag shows the minute's count above the
// plot; the same numbers are in the accessibility label and the by-day table.

const HEIGHT = 168;
const PAD = { top: 22, right: 10, bottom: 24, left: 40 };
const MINUTE_MS = 60000;

const compact = (v) => (v >= 1000 ? `${Math.round(v / 100) / 10}K` : String(v));

const LiveViewersChart = ({ session, timezone }) => {
  const [width, setWidth] = useState(0);
  const [active, setActive] = useState(null);
  const { values, start, peak, peakAt } = session;
  const n = values.length;
  const ticks = useMemo(() => yTicks(peak), [peak]);
  const top = ticks[ticks.length - 1];
  const plotW = Math.max(1, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const x = (i) => PAD.left + (n > 1 ? (i / (n - 1)) * plotW : plotW / 2);
  const y = (v) => PAD.top + plotH - (v / top) * plotH;
  const time = (i) => atEventTime(start + i * MINUTE_MS, timezone);
  const timeLabel = (i) => time(i).format(t("h:mm A"));

  const paths = useMemo(() => {
    if (!width) return null;
    let line = "";
    values.forEach((v, i) => {
      line += `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
    });
    const base = y(0).toFixed(1);
    const area = `${line}L${x(n - 1).toFixed(1)},${base}L${x(0).toFixed(1)},${base}Z`;
    return { line, area };
  }, [width, values, top]);

  // Up to four times along the bottom, the first and last at the ends.
  const xLabels = useMemo(() => {
    if (!width || n < 2) return n === 1 ? [0] : [];
    const count = Math.max(2, Math.min(4, Math.floor(plotW / 90)));
    return Array.from({ length: count }, (_, k) =>
      Math.round((k / (count - 1)) * (n - 1))
    );
  }, [width, n]);

  const peakIndex = Math.round((peakAt - start) / MINUTE_MS);
  const pick = (locationX) => {
    if (!width || locationX == null) return;
    const i = Math.round(((locationX - PAD.left) / plotW) * (n - 1));
    setActive(Math.max(0, Math.min(n - 1, i)));
  };
  const shown = active ?? peakIndex;

  const summary = t(
    "Live viewers per minute from {start} to {end}. Peak: {peak} at {time}.",
    {
      start: timeLabel(0),
      end: timeLabel(n - 1),
      peak: formatCount(peak),
      time: timeLabel(peakIndex),
    }
  );

  return (
    <View>
      <Text style={[text.hint, tw`mb-1`]} numberOfLines={1}>
        {active === null
          ? t("Peak: {count} at {time}", {
              count: formatCount(peak),
              time: timeLabel(peakIndex),
            })
          : tn("{count} viewer at {time}", "{count} viewers at {time}", values[active], {
              count: formatCount(values[active]),
              time: timeLabel(active),
            })}
      </Text>
      <View
        accessible
        accessibilityRole="image"
        accessibilityLabel={summary}
        onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
        style={{ height: HEIGHT }}
        onStartShouldSetResponder={() => true}
        onResponderGrant={(e) => pick(e.nativeEvent.locationX)}
        onResponderMove={(e) => pick(e.nativeEvent.locationX)}
        onPointerMove={
          Platform.OS === "web"
            ? (e) => {
                if (e.nativeEvent.pointerType === "mouse") {
                  pick(e.nativeEvent.offsetX ?? e.nativeEvent.locationX);
                }
              }
            : undefined
        }
        onPointerLeave={Platform.OS === "web" ? () => setActive(null) : undefined}
      >
        {!!paths && (
          <Svg width={width} height={HEIGHT} pointerEvents="none">
            {ticks.map((v) => (
              <Line
                key={v}
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y(v)}
                y2={y(v)}
                stroke={colors.divider}
                strokeWidth={1}
              />
            ))}
            <Path d={paths.area} fill={colors.slate} fillOpacity={0.1} />
            <Path
              d={paths.line}
              fill="none"
              stroke={colors.slate}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {active !== null && (
              <Line
                x1={x(active)}
                x2={x(active)}
                y1={PAD.top}
                y2={PAD.top + plotH}
                stroke={colors.muted}
                strokeWidth={1}
              />
            )}
            <Circle
              cx={x(shown)}
              cy={y(values[shown])}
              r={4}
              fill={colors.slate}
              stroke={colors.white}
              strokeWidth={2}
            />
          </Svg>
        )}
        {!!width &&
          ticks.map((v) => (
            <Text
              key={`y${v}`}
              pointerEvents="none"
              style={[
                text.hint,
                tw`absolute text-right`,
                { left: 0, width: PAD.left - 6, top: y(v) - 9 },
              ]}
            >
              {compact(v)}
            </Text>
          ))}
        {xLabels.map((i, k) => (
          <Text
            key={`x${i}`}
            pointerEvents="none"
            numberOfLines={1}
            style={[
              text.hint,
              tw`absolute`,
              {
                top: HEIGHT - PAD.bottom + 4,
                width: 80,
                left:
                  k === 0
                    ? PAD.left
                    : k === xLabels.length - 1
                    ? width - PAD.right - 80
                    : x(i) - 40,
                textAlign:
                  k === 0 ? "left" : k === xLabels.length - 1 ? "right" : "center",
              },
            ]}
          >
            {timeLabel(i)}
          </Text>
        ))}
      </View>
    </View>
  );
};

export default LiveViewersChart;
